import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { hrApi } from '@/api';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { HrAttendanceSegmentBreakdown } from '@/modules/hr/components/HrAttendanceSegmentBreakdown';
import {
  hrFormatAttendanceClock,
  hrFormatMinutes,
  hrAttendanceRowMinutes,
} from '@/modules/hr/components/hrCalendarUtils';
import { hrIsBusinessActiveStatus } from '@/modules/hr/components/hrApprovalMetricsUtils';
import { HrWorkflowStatusIcon } from '@/modules/hr/components/HrWorkflowStatusIcon';
import { tierOptions } from '@/lib/query';
import { cn } from '@/utils/cn';

type Props = {
  attendanceId: number;
  compact?: boolean;
  className?: string;
};

function attendanceModeBadgeVariant(mode?: string): 'default' | 'success' | 'warning' | 'info' {
  switch (mode?.trim().toUpperCase()) {
    case 'GEO':
      return 'success';
    case 'WEB':
    case 'MOBILE':
      return 'info';
    case 'BIOMETRIC':
    case 'RFID':
    case 'QR':
      return 'warning';
    default:
      return 'default';
  }
}

function attendanceModeLabel(mode: string | null | undefined, t: TFunction) {
  const normalized = mode?.trim().toUpperCase() || 'MANUAL';
  const fallback = normalized
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (letter) => letter.toUpperCase());
  return t(`attendance.modes.${normalized}`, { defaultValue: fallback });
}

/**
 * Neat attendance summary + hours breakdown for approval timelines
 * (same layout as the calendar day popup).
 */
export function AttendanceApprovalContextDetails({
  attendanceId,
  compact = false,
  className,
}: Props) {
  const { t } = useTranslation(['hr', 'workflow']);
  const { data: attendance, isLoading } = useQuery({
    queryKey: ['hr', 'attendance', attendanceId, 'context-details'],
    queryFn: () => hrApi.getAttendance(attendanceId),
    enabled: attendanceId > 0,
    ...tierOptions('operational'),
  });

  if (isLoading) {
    return <Skeleton className={cn('h-24 w-full rounded-lg', className)} />;
  }

  if (!attendance) return null;

  const approved = hrIsBusinessActiveStatus(attendance.approvalStatus);

  return (
    <div
      className={cn(
        compact
          ? 'space-y-2'
          : 'rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/40',
        className,
      )}
    >
      <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-slate-500">{t('attendance.detail.approval')}</dt>
        <dd className="font-medium text-slate-900 dark:text-slate-100">
          <span className="inline-flex items-center gap-1.5">
            <HrWorkflowStatusIcon
              statusCode={approved ? 'APPROVED' : attendance.approvalStatus ?? 'PENDING'}
            />
            {approved
              ? t('status.approved', { ns: 'workflow' })
              : attendance.approvalStatus ?? t('attendance.actions.pendingApproval')}
          </span>
        </dd>
        <dt className="text-slate-500">{t('attendance.detail.status')}</dt>
        <dd className="font-medium text-slate-900 dark:text-slate-100">
          {attendance.attendanceStatus}
        </dd>
        <dt className="text-slate-500">{t('attendance.detail.checkIn')}</dt>
        <dd>{hrFormatAttendanceClock(attendance.checkIn)}</dd>
        <dt className="text-slate-500">{t('attendance.detail.source')}</dt>
        <dd>
          <Badge variant={attendanceModeBadgeVariant(attendance.checkInMode)}>
            {attendanceModeLabel(attendance.checkInMode, t)}
          </Badge>
        </dd>
        <dt className="text-slate-500">{t('attendance.detail.checkOut')}</dt>
        <dd>{hrFormatAttendanceClock(attendance.checkOut)}</dd>
        <dt className="text-slate-500">{t('attendance.detail.worked')}</dt>
        <dd>
          <span className="inline-flex items-center gap-1.5">
            <HrWorkflowStatusIcon
              statusCode={approved ? 'APPROVED' : attendance.approvalStatus ?? 'PENDING'}
            />
            {hrFormatMinutes(hrAttendanceRowMinutes(attendance))}
          </span>
        </dd>
        {(attendance.overtimeMinutes ?? 0) > 0 ? (
          <>
            <dt className="text-slate-500">{t('attendance.kind.overtime')}</dt>
            <dd>
              {hrFormatMinutes(attendance.overtimeMinutes)}
              {attendance.overtimeType ? ` · ${attendance.overtimeType}` : ''}
            </dd>
          </>
        ) : null}
      </dl>
      <HrAttendanceSegmentBreakdown
        attendanceId={attendance.id}
        enabled={Boolean(attendance.checkOut)}
      />
    </div>
  );
}
